import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { ApiError, api, getToken, setAuthFailureHandler, setToken } from '../lib/api'

export interface User {
  id: number
  email: string
  role: string
  created_at: string
}

interface AuthResponse {
  token: string
  user: User
}

/**
 * Why the session is gone, when it is gone involuntarily.
 *
 * `null` means the user is simply signed out — at boot, or because they asked.
 * A value means something happened *to* them and the sign-in screen owes them a
 * sentence about it. The distinction matters: showing "your session ended" to
 * somebody who has never signed in is noise, and showing nothing to somebody who
 * was signed out mid-tap is the app losing their trust quietly.
 */
export type SignedOutReason = 'revoked' | 'expired' | null

/**
 * `loading` is a real state, not an absence.
 *
 * The session is not known until `/api/auth/me` answers, and rendering the
 * sign-in screen in the meantime shows an authenticated user their own login
 * form for a frame — the "flash of login" the acceptance criteria name. Callers
 * branch on three states, and the router shows a splash for the first.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

interface AuthValue {
  status: AuthStatus
  user: User | null
  signedOutReason: SignedOutReason
  register: (email: string, password: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  /** Clears the "your session ended" notice once the user has seen it. */
  acknowledgeSignedOut: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * Session state, and the one place a token is written.
 *
 * SUAP-0202. The api client (SUAP-0101) already stores the token and already
 * calls a handler on a dead session; this is what it was waiting for.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() => (getToken() === null ? 'anonymous' : 'loading'))
  const [user, setUser] = useState<User | null>(null)
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason>(null)

  /**
   * Guards the sign-out path against re-entry.
   *
   * `setAuthFailureHandler` fires from inside the api client's error path, and
   * every request can reach it. Without this, a dead token on a screen that
   * makes two calls signs out twice, and any sign-out that itself made a request
   * would loop — which is the acceptance criterion's "does not retry in a loop".
   * Nothing in `forceSignOut` calls the API, and this makes that structural
   * rather than a property of the current implementation.
   */
  const signingOut = useRef(false)

  const forceSignOut = useCallback((reason: SignedOutReason) => {
    if (signingOut.current) return
    signingOut.current = true

    setToken(null)
    setUser(null)
    setSignedOutReason(reason)
    setStatus('anonymous')

    // Released on the next tick rather than immediately: any in-flight requests
    // that were already going to fail land in the same turn, and they should
    // find the guard closed.
    queueMicrotask(() => {
      signingOut.current = false
    })
  }, [])

  // Hydration. Runs once, and its outcome is the difference between "signed in
  // across a refresh" and "signed out every time the tab reloads".
  useEffect(() => {
    if (getToken() === null) return

    let cancelled = false
    api
      .get<User>('/api/auth/me')
      .then((me) => {
        if (cancelled) return
        setUser(me)
        setStatus('authenticated')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // A token that no longer works at boot is not an incident: it expired
        // while the tab was closed. Clear it and say nothing — `forceSignOut`
        // has already run via the api client's handler, and passing a reason
        // here would show "your session ended" to somebody who just opened the
        // app tomorrow.
        //
        // Anything else — offline, a 500 — must NOT clear the token: the session
        // may be perfectly good and the network is not. Signing out on a flaky
        // connection loses a user's session for no reason.
        if (error instanceof ApiError && error.isAuthFailure) {
          setToken(null)
          setUser(null)
          setStatus('anonymous')
          return
        }
        setStatus('anonymous')
      })

    return () => {
      cancelled = true
    }
  }, [])

  // The api client's dead-session hook. Registered for the life of the provider.
  useEffect(() => {
    setAuthFailureHandler(() => forceSignOut('revoked'))
    return () => setAuthFailureHandler(null)
  }, [forceSignOut])

  const adopt = useCallback((response: AuthResponse) => {
    setToken(response.token)
    setUser(response.user)
    setSignedOutReason(null)
    setStatus('authenticated')
  }, [])

  const register = useCallback(
    async (email: string, password: string) => {
      adopt(await api.post<AuthResponse>('/auth/register', { email, password }))
    },
    [adopt],
  )

  const signIn = useCallback(
    async (email: string, password: string) => {
      adopt(await api.post<AuthResponse>('/auth/login', { email, password }))
    },
    [adopt],
  )

  // Deliberate sign-out carries no reason: the user knows why.
  const signOut = useCallback(() => {
    setToken(null)
    setUser(null)
    setSignedOutReason(null)
    setStatus('anonymous')
  }, [])

  const acknowledgeSignedOut = useCallback(() => setSignedOutReason(null), [])

  const value = useMemo<AuthValue>(
    () => ({ status, user, signedOutReason, register, signIn, signOut, acknowledgeSignedOut }),
    [status, user, signedOutReason, register, signIn, signOut, acknowledgeSignedOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** The session. Throws outside a provider, which is a wiring bug, not a state. */
export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth must be used inside an AuthProvider')
  return value
}
